// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LeaseAgreementModal from "@guestsupply/App/Components/Cart/LeaseAgreementModal";
import Notify from "@guestsupply/App/Components/Notify";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { hasProductsWithInvalidPrice } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { useState } from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: ApplicationState) => ({
    cart: getCurrentCartState(state).value,
});

export interface CartApproverMessageStyles {
    approvingText?: TypographyPresentationProps;
    approvingWrapper?: InjectableCss;
    // XNG-CHANGES
    container?: InjectableCss;
    clickHereLink?: LinkPresentationProps;
}
// XNG-CHANGES: Removing styles
export const cartApproverMessageStyles: CartApproverMessageStyles = {};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const styles = cartApproverMessageStyles;
const CartApproverMessage = ({ cart }: Props) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    if (!cart || cart.cartLines?.length === 0) {
        return null;
    }

    const handleModaleClose = () => {
        setIsModalOpen(false);
    };

    const productsCannotBePurchased = cart?.cartLines?.some(o => o.isRestricted || !o.isActive || o.cantBuy) ?? false;

    const invalidPriceAtCheckout = hasProductsWithInvalidPrice(cart);

    let shippingApiCallError;
    if (cart?.properties["additionalShippingRateInfo"]) {
        const addlShippingRateInfo = JSON.parse(cart.properties["additionalShippingRateInfo"]);
        shippingApiCallError = addlShippingRateInfo.taxAndShippingApiResult.isAllShippingCallsSuccessful === false;
    }

    let minDropShipThresholdMessages: string[] = [];
    if (cart?.properties["minDropShipThresholdMessages"]) {
        minDropShipThresholdMessages = JSON.parse(cart.properties["minDropShipThresholdMessages"]);
    }

    let gpLeaseAgreementMessages: string[] = [];
    if (cart?.properties["leaseItemsMessages"]) {
        gpLeaseAgreementMessages = JSON.parse(cart.properties["leaseItemsMessages"]);
    }
    // XNG-CHANGES : Adding classic messages
    return (
        <StyledWrapper {...styles.container}>
            {cart?.isAwaitingApproval && (
                <Notify {...{ fullWidth: true }} type="info" data-test-selector="cartPage_approvingMessage">
                    {cart.isAwaitingApproval && (
                        <Typography {...styles.approvingText} data-test-selector="cart_approvingOrderMessage">
                            {translate("Approving Order for {0}", cart.initiatedByUserName?.toUpperCase())}
                        </Typography>
                    )}
                </Notify>
            )}
            {cart?.requiresApproval && !cart?.hasApprover && (
                <Notify {...{ fullWidth: true }} type="info" data-test-selector="cartPage_approvingMessage">
                    {siteMessage("OrderApproval_BuyerWithoutApprover")}
                </Notify>
            )}
            {cart && cart?.quoteRequiredCount > 0 && (
                <Notify {...{ fullWidth: true }} type="info" data-test-selector="cartPage_QuoteRequiredMessage">
                    {siteMessage("Rfq_QuoteRequiredMsg")}
                </Notify>
            )}
            {cart?.properties["someCartLinesNotPriced"] === "true" && (
                <Notify {...{ fullWidth: true }} type="warning" data-test-selector="cartPage_itemNotPriced">
                    {siteMessage(
                        "GUEST_Cart_Items_Not_Priced_Msg",
                        "Some of the items in your shopping cart could not be priced. Please remove these items or save your order and try again later.",
                    )}
                </Notify>
            )}
            {cart?.properties["someCartLinesNotValidForWarehouse"] === "true" && (
                <Notify
                    {...{ fullWidth: true }}
                    type="warning"
                    data-test-selector="cartPage_someCartLinesNotValidForWarehouseMessage"
                >
                    {siteMessage(
                        "Guest_CartError_Some_CartLines_Not_Valid_For_Warehouse",
                        "Some of the items in your shopping cart are not available to be shipped to your postal code. Please remove these items to continue.",
                    )}
                </Notify>
            )}
            {cart?.properties["hasBidItem"] === "true" && (
                <Notify {...{ fullWidth: true }} type="warning" data-test-selector="cartPage_hasBidItemMessage">
                    {siteMessage("Cart_InsufficientInventoryAtCheckout")}
                </Notify>
            )}
            {cart?.hasInsufficientInventory && (
                <Notify
                    {...{ fullWidth: true }}
                    type="danger"
                    data-test-selector="cartPage_hasInsufficientInventoryMessage"
                >
                    {siteMessage("cartPage_hasInsufficientInventory_message")}
                </Notify>
            )}
            {productsCannotBePurchased && (
                <Notify
                    {...{ fullWidth: true }}
                    type="danger"
                    data-test-selector="cartPage_ProductsCannotBePurchasedMessage"
                >
                    {siteMessage("Cart_ProductsCannotBePurchased")}
                </Notify>
            )}
            {!cart?.cartNotPriced && invalidPriceAtCheckout && (
                <Notify
                    {...{ fullWidth: true }}
                    type="danger"
                    data-test-selector="cartPage_InvalidPriceAtCheckoutMessage"
                >
                    {siteMessage("Cart_InvalidPriceAtCheckout")}
                </Notify>
            )}
            {cart?.cartNotPriced && (
                <Notify
                    {...{ fullWidth: true }}
                    type="danger"
                    data-test-selector="cartPage_NoPriceAvailableAtCheckoutMessage"
                >
                    {siteMessage("Cart_NoPriceAvailableAtCheckout")}
                </Notify>
            )}
            {cart?.failedToGetRealTimeInventory && (
                <Notify
                    {...{ fullWidth: true }}
                    type="danger"
                    data-test-selector="cartPage_NoInventoryAvailableAtCheckoutMessage"
                >
                    {siteMessage("Cart_NoInventoryAvailableAtCheckout")}
                </Notify>
            )}
            {shippingApiCallError && (
                <Notify
                    {...{ fullWidth: true }}
                    type="warning"
                    data-test-selector="cartPage_shippingApiCallErrorMessage"
                >
                    {siteMessage(
                        "GUEST_Cart_ShippingApiCallError",
                        "There was a problem calculating freight costs for one or more of your shipments. You may proceed with your order, but freight costs may be adjusted later. Please contact customer support for assistance.",
                    )}
                </Notify>
            )}
            {minDropShipThresholdMessages?.map((message, id) => {
                return (
                    <Notify
                        key={id}
                        {...{ fullWidth: true }}
                        type="danger"
                        data-test-selector="cartPage_minDropShipThresholdMessages"
                    >
                        {message}
                    </Notify>
                );
            })}
            {cart?.properties["gpLeaseRequired"]?.toLowerCase() === "true" &&
                cart.properties["gpLeaseAccepted"]?.toLowerCase() !== "true" &&
                gpLeaseAgreementMessages?.map((message, id) => {
                    return (
                        <Notify
                            key={id}
                            {...{ fullWidth: true }}
                            type="danger"
                            data-test-selector="cartPage_gpLeaseRequiredMessage"
                        >
                            <StyledWrapper>
                                <Typography>{message}</Typography>
                                <Link
                                    {...styles.clickHereLink}
                                    onClick={() => {
                                        setIsModalOpen(true);
                                    }}
                                    aria-label="click here"
                                >
                                    {translate("Click Here")}
                                </Link>
                                <LeaseAgreementModal isModalOpen={isModalOpen} handleModaleClose={handleModaleClose} />
                            </StyledWrapper>
                        </Notify>
                    );
                })}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CartApproverMessage),
    definition: {
        displayName: "Cart Approver Message",
        icon: "cart-circle-check",
        group: "Common",
    },
};

export default widgetModule;
